import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  List,
  ListItem,
  ListItemText,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import {
  portalErrorMessage,
  sendBulkPortalEmails,
  sendManagedClubBulkEmails
} from '../../features/portal/portalService';

const portalKey = (portal) =>
  `${portal.kind}:${portal.venueId || ''}`;

const portalLabel = (portal) =>
  portal.kind === 'secretary'
    ? 'Tournament Secretary'
    : portal.venueName || 'Club';

const PortalBulkEmailDialog = ({
  open,
  onClose,
  mode,
  token,
  eventId,
  portals = [],
  limits = {},
  onDone
}) => {
  const isPortal = mode === 'portal';
  const maxPortals = Number(limits.bulkPortals) || 20;
  const maxRecipients = Number(limits.bulkRecipients) || 100;
  const [selected, setSelected] = useState(new Set());
  const [pinMode, setPinMode] = useState('missing');
  const [regenerateConfirmed, setRegenerateConfirmed] = useState(false);
  const [selectionError, setSelectionError] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const wasOpen = useRef(false);

  const withContacts = useMemo(
    () => portals.filter((portal) => Number(portal.contactCount) > 0),
    [portals]
  );

  useEffect(() => {
    if (open && !wasOpen.current) {
      setSelected(
        new Set(withContacts.slice(0, maxPortals).map((portal) => portalKey(portal)))
      );
      setPinMode('missing');
      setRegenerateConfirmed(false);
      setSelectionError('');
      setSendError('');
      setResult(null);
      setCopied(false);
    }
    wasOpen.current = open;
  }, [open, withContacts, maxPortals]);

  const selectedPortals = useMemo(
    () => portals.filter((portal) => selected.has(portalKey(portal))),
    [portals, selected]
  );
  const recipientCount = selectedPortals.reduce(
    (total, portal) => total + (Number(portal.contactCount) || 0),
    0
  );
  const allSelectable = withContacts.slice(0, maxPortals);
  const allSelected =
    allSelectable.length > 0 &&
    allSelectable.every((portal) => selected.has(portalKey(portal)));
  const someSelected =
    !allSelected &&
    allSelectable.some((portal) => selected.has(portalKey(portal)));
  const selectedWithPin = selectedPortals.filter((portal) => portal.hasPin);
  const selectedSharedOnly = selectedPortals.filter(
    (portal) =>
      Number(portal.contactCount) > 0 && portal.personalAvailable === false
  );
  const zeroContactPortals = portals.filter(
    (portal) => Number(portal.contactCount) <= 0
  );
  const tooManyRecipients = recipientCount > maxRecipients;

  const toggleAll = () => {
    setSelectionError('');
    setSelected(
      allSelected
        ? new Set()
        : new Set(allSelectable.map((portal) => portalKey(portal)))
    );
  };

  const togglePortal = (portal) => {
    const key = portalKey(portal);
    const next = new Set(selected);

    if (next.has(key)) {
      next.delete(key);
    } else {
      if (next.size >= maxPortals) {
        setSelectionError(`Select at most ${maxPortals} portals per send.`);
        return;
      }
      next.add(key);
    }

    setSelectionError('');
    setSelected(next);
  };

  const handleSend = async () => {
    if (sending || selectedPortals.length === 0 || tooManyRecipients) return;
    if (pinMode === 'regenerate' && !regenerateConfirmed) return;

    setSending(true);
    setSendError('');
    setCopied(false);

    const payload = {
      url: window.location.origin,
      regeneratePins: pinMode === 'regenerate',
      targets: selectedPortals.map((portal) => ({
        kind: portal.kind,
        venueId: portal.kind === 'club' ? portal.venueId : null
      }))
    };

    try {
      const response = isPortal
        ? await sendManagedClubBulkEmails(token, payload)
        : await sendBulkPortalEmails(eventId, payload);
      setResult(response);
    } catch (requestError) {
      setSendError(
        portalErrorMessage(
          requestError,
          'The emails could not be sent. Please try again.'
        )
      );
    } finally {
      setSending(false);
    }
  };

  const closeDialog = () => {
    if (sending) return;
    if (result) onDone?.();
    onClose();
  };

  const finish = () => {
    onDone?.();
    onClose();
  };

  const newPins = (result?.results || []).filter(
    (row) => row.pin && row.pinGenerated
  );
  const pinText = newPins
    .map((row) => `${row.venueName || portalLabel(row)}: ${row.pin}`)
    .join('\n');
  const failedRecipients = (result?.results || []).flatMap((row) =>
    (row.failed || []).map((failed) => ({
      ...failed,
      venueName: row.venueName || portalLabel(row)
    }))
  );
  const portalErrors = (result?.results || []).filter((row) => row.error);
  const bulkWarnings = [
    ...new Set((result?.results || []).flatMap((row) => row.warnings || []))
  ];
  const summary = result?.summary || {};
  let summaryText = 'No emails were sent.';
  if (summary.sentCount > 0 && summary.failedCount > 0) {
    summaryText = `Sent ${summary.sentCount} email${
      summary.sentCount === 1 ? '' : 's'
    }; ${summary.failedCount} failed.`;
  } else if (summary.sentCount > 0) {
    summaryText = `Sent ${summary.sentCount} email${
      summary.sentCount === 1 ? '' : 's'
    } to ${summary.portalCount} portal${summary.portalCount === 1 ? '' : 's'}.`;
  } else if (summary.failedCount > 0) {
    summaryText = `No emails were sent. ${summary.failedCount} failed.`;
  }

  const copyPins = async () => {
    if (!pinText) return;
    try {
      await navigator.clipboard.writeText(pinText);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const sendDisabled =
    sending ||
    selectedPortals.length === 0 ||
    recipientCount === 0 ||
    tooManyRecipients ||
    (pinMode === 'regenerate' && !regenerateConfirmed);

  return (
    <Dialog
      open={open}
      onClose={sending ? undefined : closeDialog}
      maxWidth="sm"
      fullWidth
      aria-labelledby="portal-bulk-email-title">
      <DialogTitle id="portal-bulk-email-title">
        {result ? 'Bulk email results' : 'Email portal details'}
      </DialogTitle>

      {result ? (
        <>
          <DialogContent>
            <Alert
              severity={
                result.success
                  ? 'success'
                  : result.partialSuccess
                    ? 'warning'
                    : 'error'
              }
              sx={{ mb: 2 }}>
              {summaryText}
            </Alert>

            {newPins.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                  mb={1}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    New PINs
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<ContentCopyIcon />}
                    onClick={copyPins}>
                    {copied ? 'Copied' : 'Copy all'}
                  </Button>
                </Stack>
                <Stack spacing={0.75}>
                  {newPins.map((row) => (
                    <Stack
                      key={`${row.kind}:${row.venueId || ''}`}
                      direction="row"
                      justifyContent="space-between"
                      spacing={1}>
                      <Typography variant="body2" sx={{ minWidth: 0 }}>
                        {row.venueName || portalLabel(row)}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        fontWeight={700}
                        letterSpacing={2}>
                        {row.pin}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                  Copy or send these PINs now. You can open Link &amp; QR later to
                  send a PIN again without replacing it.
                </Alert>
              </Paper>
            )}

            {portalErrors.length > 0 && (
              <Box mb={2}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Portal errors
                </Typography>
                <Stack spacing={0.5}>
                  {portalErrors.map((row) => (
                    <Typography
                      key={`${row.kind}:${row.venueId || ''}`}
                      variant="body2"
                      color="error.main">
                      {row.venueName || portalLabel(row)}: {row.error}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}

            {failedRecipients.length > 0 && (
              <Box mb={2}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Failed recipients
                </Typography>
                <List dense disablePadding>
                  {failedRecipients.map((failed) => (
                    <ListItem key={`${failed.venueName}:${failed.email}`} disableGutters>
                      <ListItemText
                        primary={failed.email}
                        secondary={`${failed.venueName} · ${
                          failed.error || 'The email could not be sent.'
                        }`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {bulkWarnings.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {bulkWarnings.map((warning) => (
                  <Typography key={warning} variant="body2" display="block">
                    {warning}
                  </Typography>
                ))}
              </Alert>
            )}

            {summary.personalSentCount > 0 && (
              <Typography variant="body2" color="text.secondary" display="block">
                Personal PINs were emailed to {summary.personalSentCount} contact
                {summary.personalSentCount === 1 ? '' : 's'}.
              </Typography>
            )}
            {summary.personalReusedCount > 0 && (
              <Typography variant="body2" color="text.secondary" display="block">
                {summary.personalReusedCount} contact
                {summary.personalReusedCount === 1 ? '' : 's'} already had a personal
                PIN; {summary.personalReusedCount === 1 ? 'it was' : 'they were'}{' '}
                re-sent unchanged.
              </Typography>
            )}
            {summary.sharedSentCount > 0 && (
              <Typography variant="body2" color="text.secondary" display="block">
                {summary.sharedSentCount} recipient
                {summary.sharedSentCount === 1 ? '' : 's'} received the shared link
                details.
              </Typography>
            )}
            {summary.generatedPinCount > 0 && (
              <Typography variant="body2" color="text.secondary" display="block">
                {summary.generatedPinCount} portal
                {summary.generatedPinCount === 1 ? '' : 's'} received a new shared PIN.
              </Typography>
            )}
            {summary.existingPinCount > 0 && (
              <Typography variant="body2" color="text.secondary" display="block">
                {summary.existingPinCount} portal
                {summary.existingPinCount === 1 ? '' : 's'} used an existing shared PIN.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={finish}>
              Done
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Send the portal link and access details to every contact at the
              selected portals. Saved contacts keep their existing personal PIN;
              one is issued only when they do not have one yet.
            </Typography>

            {selectedSharedOnly.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {selectedSharedOnly.length === 1
                  ? `${portalLabel(
                      selectedSharedOnly[0]
                    )} cannot issue personal PINs right now; its contacts will receive the shared PIN.`
                  : `${selectedSharedOnly.length} selected portals cannot issue personal PINs right now; their contacts will receive the shared PIN.`}
              </Alert>
            )}

            {withContacts.length === 0 ? (
              <Alert severity="info">
                No selected portal has any contacts yet. Add contacts first.
              </Alert>
            ) : (
              <>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                  mb={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={toggleAll}
                      />
                    }
                    label="Select all portals with contacts"
                  />
                  <Typography variant="caption" color="text.secondary">
                    {selected.size} of {maxPortals} selected
                  </Typography>
                </Stack>

                <Paper variant="outlined" sx={{ maxHeight: 260, overflow: 'auto', mb: 1 }}>
                  <List dense disablePadding>
                    {portals.map((portal) => {
                      const key = portalKey(portal);
                      const checked = selected.has(key);
                      const noContacts = Number(portal.contactCount) <= 0;
                      const atLimit = !checked && selected.size >= maxPortals;

                      return (
                        <ListItem key={key} disableGutters sx={{ px: 1.5 }}>
                          <FormControlLabel
                            sx={{ flex: 1, minWidth: 0, m: 0 }}
                            control={
                              <Checkbox
                                checked={checked}
                                onChange={() => togglePortal(portal)}
                                disabled={noContacts || atLimit}
                              />
                            }
                            label={
                              <Box>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                  flexWrap="wrap"
                                  useFlexGap>
                                  <Typography variant="body2" fontWeight={600}>
                                    {portalLabel(portal)}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={
                                      noContacts
                                        ? 'No contacts yet'
                                        : `${portal.contactCount} contact${
                                            portal.contactCount === 1 ? '' : 's'
                                          }`
                                    }
                                  />
                                  {portal.hasPin && (
                                    <Chip
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                      label="PIN set"
                                    />
                                  )}
                                  {typeof portal.personalAvailable === 'boolean' && (
                                    <Chip
                                      size="small"
                                      color={portal.personalAvailable ? 'success' : 'default'}
                                      variant="outlined"
                                      label={
                                        portal.personalAvailable
                                          ? 'Personal PINs'
                                          : 'Shared PIN only'
                                      }
                                    />
                                  )}
                                </Stack>
                                {portal.shared && (
                                  <Typography variant="caption" color="text.secondary">
                                    Shared portal
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Paper>

                {selectionError && (
                  <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setSelectionError('')}>
                    {selectionError}
                  </Alert>
                )}

                <Typography variant="body2" color="text.secondary" mb={2}>
                  {recipientCount} email{recipientCount === 1 ? '' : 's'} will be sent.
                  {zeroContactPortals.length > 0
                    ? ' Portals without contacts are skipped.'
                    : ''}
                </Typography>

                {tooManyRecipients && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    This selection has {recipientCount} recipients. Send at most{' '}
                    {maxRecipients} emails at a time.
                  </Alert>
                )}

                <Divider sx={{ mb: 2 }} />

                <FormControl>
                  <FormLabel id="portal-pin-mode-label">PIN handling</FormLabel>
                  <RadioGroup
                    aria-labelledby="portal-pin-mode-label"
                    value={pinMode}
                    onChange={(event) => {
                      setPinMode(event.target.value);
                      setRegenerateConfirmed(false);
                    }}>
                    <FormControlLabel
                      value="missing"
                      control={<Radio />}
                      label="Create a PIN only for portals that don’t have one"
                    />
                    <FormControlLabel
                      value="regenerate"
                      control={<Radio />}
                      label="Replace every selected portal’s PIN"
                    />
                  </RadioGroup>
                </FormControl>

                {pinMode === 'missing' && selectedWithPin.length > 0 && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {selectedWithPin.length} selected portal
                    {selectedWithPin.length === 1 ? ' already has' : 's already have'} a
                    shared PIN. Saved contacts keep their own personal PIN; other
                    recipients receive the shared PIN when it can be recovered.
                  </Alert>
                )}

                {pinMode === 'regenerate' && (
                  <>
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      The existing shared PINs stop working immediately, every
                      device signed in to them is signed out, and previously
                      emailed shared PINs are invalid. Personal PINs stay valid.
                      The new shared PINs are shown once after sending.
                    </Alert>
                    <FormControlLabel
                      sx={{ mt: 1 }}
                      control={
                        <Checkbox
                          checked={regenerateConfirmed}
                          onChange={(event) =>
                            setRegenerateConfirmed(event.target.checked)
                          }
                        />
                      }
                      label="I understand and want to replace the existing PINs"
                    />
                  </>
                )}

                {sendError && (
                  <Alert severity="error" sx={{ mt: 2 }} onClose={() => setSendError('')}>
                    {sendError}
                  </Alert>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog} disabled={sending}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={
                sending ? <CircularProgress size={18} color="inherit" /> : <EmailIcon />
              }
              onClick={handleSend}
              disabled={sendDisabled || withContacts.length === 0}>
              {sending ? 'Sending…' : 'Send emails'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default PortalBulkEmailDialog;
